import { COUNTRIES_CODES, PENCE_IN_POUND, PILLAR_COLOURS } from '../constants'

// Used: useCurrency, tests
const CURRENCIES = { EUR: '€', GBP: '£' }
export const getCurrencySymbol = code => CURRENCIES[code] || CURRENCIES.GBP

// Used: Orders & Resource Ordering
export const getTypeVerb = type => {
    const VERBS = {
        Article: 'Read article',
        Audio: 'Listen now',
        Course: 'Get course',
        Event: 'Get event',
        Podcast: 'Listen now',
        Video: 'Watch now',
    }
    switch (type) {
        case 'Activity':
        case 'Charity':
        case 'Garden':
        case 'Leave':
        case 'Museum':
        case 'Nature reserve':
        case 'Organisation':
        case 'Other':
        case 'Phone Line':
        case 'Volunteering':
        case 'Website':
            return 'Learn more'
        default:
            return VERBS[type] || 'Get access'
    }
}

/**
 * @param {string | number} price
 * @returns price as string with or without currency symbol: "£0.00", '£12.00' || '12.00' || ''
 */
export const formatPrice = (price, symbol = true) => {
    if (price === null || price === undefined) return ''

    const priceInPence = typeof price === 'number' ? price : parseFloat(price)

    if (isNaN(priceInPence)) {
        console.warn('[formatPrice] priceInPence', priceInPence)
        return ''
    }

    const pounds = (priceInPence / PENCE_IN_POUND).toFixed(2)

    return symbol ? `${'£'}${pounds}` : pounds
}

/**
 * Used: S/Transaction & tests
 * @param {object} transaction
 * @returns string
 */
export const getTransactionStateText = ({ state, voucherId }) => {
    const STATES = {
        CANCELLED: 'Cancelled',
        CONFIRMED: voucherId ? 'Confirmed' : 'Dispatched',
        PENDING: 'Pending',
    }

    return STATES[state] || ''
}

// Used: W&M/Charts
export const getColorByPillar = (pillarKey = 'default', lightness = 'medium') => {
    try {
        if (!PILLAR_COLOURS[pillarKey]) {
            throw new Error(`Can not find color for "${pillarKey}" pillar. Returned default blue color`)
        } else {
            if (!PILLAR_COLOURS[pillarKey][lightness]) {
                throw new Error('Can not reed type of pillar color')
            }
        }
        return PILLAR_COLOURS[pillarKey][lightness]
    } catch (error) {
        console.warn('getColorByPillar', error)
        return PILLAR_COLOURS.blue.medium
    }
}

// Used U/DirectoryPage
export const getPillarFromURL = (location = {}) => (location.hash ? location.hash.slice(1) : '')

// Used W&M.
export const getFirstTen = (array = []) => (array.length > 10 ? array.splice(0, 10) : array)

/* Used W&M. Client/BudgetSetup */
// TODO: check on dev & prod branches. If not used can be removed
export const numberToBoolean = (object = {}) => {
    for (let key in object) {
        object[key] = !!object[key]
    }
    return object
}

/* Used W&M/nicknames. */
export const transformStringInArray = value =>
    typeof value === 'string' && value.length
        ? value
              .split(',')
              .filter(str => str.trim().length > 1)
              .map(str => str.trim())
        : value

// Used W&M. Get percentage by value
export const getPercentage = (value, all) => ((value * 100) / all).toFixed(2)

/* Used W&M. Client/BudgetSetup */
// TODO: check on dev & prod branches. If not used can be removed
export const getPeriodName = (string = '') => {
    switch (string) {
        case 'Monthly':
            return 'month'
        case 'Quarterly':
            return 'quarter'
        case 'Yearly':
            return 'year'
        default:
            return string
    }
}

/* Used W/Invoices */
export const getDataByYear = (array = []) => {
    if (!array.length) {
        return []
    }

    const years = array.map(({ date }) => date.split('-')[0])
    const uniqYears = Array.from(new Set(years))

    return uniqYears.map(year => {
        return {
            year,
            data: array.filter(({ date }) => date.split('-')[0] === year),
        }
    })
}

// Used W.
export const getDataFilteredByClient = (data = [], pilotTag) => (pilotTag ? data.filter(item => item.pilotTag === pilotTag) : data)

/* Used W. by Tickets */
export const getInitiator = key => {
    const INITIATORS = {
        ['supplier-to-user']: 'Supplier',
        ['admin-to-both']: 'Admin',
        default: 'User',
    }
    return INITIATORS[key] || INITIATORS.default
}

// Used U: Return item from array with random index
export const getRandomItem = array => array[Math.floor(Math.random() * array.length)]

// Used U.
export const getFilterTagLabel = ({ filterTagsByPillar = [], tag = '' }) => {
    const filteredTag = filterTagsByPillar.find(({ value }) => value === tag)
    return filteredTag ? filteredTag.label : tag
}

// Used U.
const COUNTRIES = {
    'United Kingdom': COUNTRIES_CODES.GB,
    'Republic of Ireland': COUNTRIES_CODES.IE,
}
export const doesCountryVATMatch = (country, code = '') => code === COUNTRIES[country]

// Used U.
export const pillarsWithTags = ({ pillarsState = [], pillarName, goalName = '', goals = [] }) => {
    let pillars = JSON.parse(JSON.stringify(pillarsState))
    if (pillarName) {
        pillars[pillarName].tags = [goalName]
    }

    if (goals.length) {
        goals.forEach(goal => {
            Object.keys(pillars).forEach(pillarKey => {
                if (goal.pillar === pillarKey && !pillars[pillarKey].tags.includes(goal.goalTag)) {
                    pillars[pillarKey].tags.unshift(goal.goalTag)
                }
            })
        })
    }

    return pillars
}

// Used U&A.
export const moveElementsToLastPosition = (array, fieldName, value) => {
    const newArray = [...array]
    let indexOfElement
    newArray.forEach((elem, index) => {
        if (elem[fieldName] === value) indexOfElement = index
    })

    if (indexOfElement || indexOfElement === 0) {
        newArray.splice(newArray.length - 1, 0, newArray.splice(indexOfElement, 1)[0])
    }

    return newArray
}

// Used: M/W -> Wellbeing/PillarDetails; fe-lib/helpers
export const getKeyByValue = (object = {}, value) => Object.keys(object).find(key => object[key] === value)

// TODO should be removed in future
// Used W&M.
export const getAverage = (low, top, max = 100) => (!low && !top ? 0 : max - top - low)

// Used for UW & UA
export const stringToColor = string => {
    let hash = 0
    let i

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash)
    }

    let color = '#'

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff
        color += `00${value.toString(16)}`.slice(-2)
    }
    /* eslint-enable no-bitwise */

    return color
}
